import React from 'react';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaBirthdayCake, FaTicketAlt, FaStar, FaQrcode, FaUser } from 'react-icons/fa';

const ClienteDetailModal = ({ cliente, onClose }) => {
    if (!cliente) return null;

    const formatDate = (dateString) => {
        if (!dateString) return 'No disponible';
        try {
            return new Date(dateString).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Fecha inválida';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Detalles del Cliente</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Información personal */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Información Personal</h3>
                            <div className="space-y-3">
                                <div>
                                    <span className="text-sm font-medium text-gray-500">Nombre completo</span>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {cliente?.nombre || 'No disponible'} {cliente?.apellido || ''}
                                    </p>
                                </div>
                                <div className="flex items-center">
                                    <FaBirthdayCake className="text-gray-400 mr-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-500">Fecha de nacimiento</span>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {formatDate(cliente?.fechaCumpleanos)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <FaUser className="text-gray-400 mr-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-500">Género</span>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {cliente?.genero === 'HOMBRE' && 'Hombre'}
                                            {cliente?.genero === 'MUJER' && 'Mujer'}
                                            {cliente?.genero === 'NO_BINARIO' && 'No binario'}
                                            {cliente?.genero === 'PREFIERO_NO_DECIRLO' && 'Prefiero no decirlo'}
                                            {!cliente?.genero && 'No especificado'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <FaQrcode className="text-gray-400 mr-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-500">Código de referido</span>
                                        <p className="mt-1 text-sm text-gray-900">{cliente?.codigoReferido || 'No disponible'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Contacto */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Información de Contacto</h3>
                            <div className="space-y-3">
                                <div className="flex items-center">
                                    <FaEnvelope className="text-gray-400 mr-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-500">Email</span>
                                        <p className="mt-1 text-sm text-gray-900">{cliente?.email || 'No disponible'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <FaPhone className="text-gray-400 mr-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-500">Teléfono</span>
                                        <p className="mt-1 text-sm text-gray-900">{cliente?.telefono || 'No disponible'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ubicación */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Ubicación</h3>
                            <div className="space-y-3">
                                <div className="flex items-center">
                                    <FaMapMarkerAlt className="text-gray-400 mr-2" />
                                    <div>
                                        <span className="text-sm font-medium text-gray-500">Dirección</span>
                                        <p className="mt-1 text-sm text-gray-900">
                                            {cliente?.geographic_info?.city || 'No disponible'}
                                            {cliente?.geographic_info?.state ? `, ${cliente.geographic_info.state}` : ''}
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            {cliente?.geographic_info?.country || 'País no disponible'}
                                            {cliente?.geographic_info?.postal_code ? ` - CP: ${cliente.geographic_info.postal_code}` : ''}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Segmentación */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Segmentación</h3>
                            <div className="space-y-3">
                                {cliente?.segments?.length > 0 ? (
                                    cliente.segments.map((segment, index) => (
                                        <div
                                            key={index}
                                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${segment?.color || 'gray'}-100 text-${segment?.color || 'gray'}-800 mr-2`}
                                        >
                                            {segment?.label || 'Segmento sin nombre'}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-sm text-gray-500">Sin segmentos asignados</p>
                                )}
                            </div>
                        </div>

                        {/* Actividad */}
                        <div className="bg-gray-50 p-4 rounded-lg col-span-2">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Actividad</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <div className="flex items-center">
                                        <FaTicketAlt className="text-gray-400 mr-2" />
                                        <span className="text-sm font-medium text-gray-500">Compras realizadas</span>
                                    </div>
                                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                                        {cliente?.tickets?.length || 0}
                                    </p>
                                </div>
                                <div>
                                    <div className="flex items-center">
                                        <FaStar className="text-gray-400 mr-2" />
                                        <span className="text-sm font-medium text-gray-500">Feedback proporcionado</span>
                                    </div>
                                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                                        {cliente?.feedbackScores?.length || 0}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Fechas */}
                        <div className="bg-gray-50 p-4 rounded-lg col-span-2">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <span className="text-sm font-medium text-gray-500">Fecha de registro</span>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {formatDate(cliente?.fechaCreacion)}
                                    </p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-500">Última actividad</span>
                                    <p className="mt-1 text-sm text-gray-900">
                                        {cliente?.tickets?.length > 0
                                            ? formatDate(cliente.tickets[cliente.tickets.length - 1]?.fecha)
                                            : 'Sin actividad'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClienteDetailModal; 